import { z } from 'zod';
import { DomainError } from '../errors.js';
import { MATERIAL_UNITS, toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Ödül (award), bölme ve değişiklik turu (ADR 0111 §D; plan §3/D). Ofis RFQ satırlarını
 * tedarikçilere böler: her satır en çok bir etkin ödüldedir (farklı satırlar farklı tedarikçilere).
 * Her ödülün kendi tur dizisi vardır: ofis önerir → tedarikçi kabul eder, karşı teklif verir ya da
 * reddeder → ofis karşı teklifi kabul eder ya da yeni öneri gönderir → kabul edilen tur ofiste
 * dört gözle kesinleşir ve PO olur. Turlar değiştirilemezdir; içerik hash'i db'de bu modülün
 * kapalı içeriğinden hesaplanır. Hata iletileri koddur; arayüz çevirir.
 */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const requiredText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const MAX_MONEY = 1_000_000_000;
const money = () =>
  z.number(typed('INVALID_NUMBER')).min(0, 'OUT_OF_RANGE').max(MAX_MONEY, 'OUT_OF_RANGE');
const quantity = () =>
  z.number(typed('INVALID_NUMBER')).gt(0, 'OUT_OF_RANGE').max(1_000_000, 'OUT_OF_RANGE');
const days = () =>
  z
    .number(typed('INVALID_NUMBER'))
    .int('INVALID_NUMBER')
    .min(0, 'OUT_OF_RANGE')
    .max(3_650, 'OUT_OF_RANGE')
    .nullish()
    .transform((value) => value ?? null);
const roundNo = () =>
  z.number(typed('INVALID_NUMBER')).int('INVALID_NUMBER').min(1, 'OUT_OF_RANGE');

/** Bir ödülde en çok satır (RFQ kalem sınırı). */
export const MAX_AWARD_LINES = 200;

export const AWARD_STATUSES = [
  /** Açılış transaction'ının ilk anı: ilk tur henüz yazılmadı; dışarıdan görünmez. */
  'draft',
  'proposed',
  'accepted',
  'countered',
  'rejected',
  'finalized',
  'cancelled',
] as const;
export type AwardStatus = (typeof AWARD_STATUSES)[number];

export const AWARD_PARTIES = ['office', 'supplier'] as const;
export type AwardParty = (typeof AWARD_PARTIES)[number];

/**
 * `select`: ofisin satır satır seçimi (doğrudan ödül; 04.11 notları D4, ADR 0166 §2). Tek turdur:
 * teklifin satırları aynen alınır, pazarlık yoktur; ödül kabul edilmiş sayılır ve PO açılır.
 */
export const AWARD_ACTIONS = [
  'propose',
  'accept',
  'counter',
  'reject',
  'accept_counter',
  'select',
] as const;

/** Ödülün türü: portal pazarlığı (ADR 0111 §D) ya da ofisin satır satır seçimi (ADR 0166 §2). */
export const AWARD_KINDS = ['negotiated', 'direct'] as const;
export type AwardKind = (typeof AWARD_KINDS)[number];
export type AwardAction = (typeof AWARD_ACTIONS)[number];

/**
 * İkame satırın seçimi (ADR 0111 §C5 → §D): tedarikçinin önerdiği başka ürün; ad, özellik ve
 * birim. Miktar ve birim fiyat satırın kendisindedir (ikame birimine).
 */
export const awardSubstituteSchema = z.strictObject({
  name: requiredText(200),
  specification: optionalText(1000),
  unit: z.enum(MATERIAL_UNITS, typed('INVALID_UNIT')),
});
export type AwardSubstitute = z.output<typeof awardSubstituteSchema>;

export const awardLineSchema = z.strictObject({
  rfqLineId: z.uuid(typed('INVALID_ID')),
  quantity: quantity(),
  unitPrice: money(),
  substitute: awardSubstituteSchema.nullish().transform((value) => value ?? null),
  note: optionalText(500),
});
export type AwardLine = z.output<typeof awardLineSchema>;

/** Turun koşulları: teslim, ödeme, garanti ve masraflar (PO'ya aynen geçer). */
export const awardTermsSchema = z.strictObject({
  leadTimeDays: days(),
  deliveryLocation: optionalText(120),
  deliveryTerms: optionalText(200),
  paymentTerms: optionalText(200),
  warranty: optionalText(200),
  discount: money().default(0),
  freight: money().default(0),
  customs: money().default(0),
  tax: money().default(0),
  serviceFee: money().default(0),
  remarks: optionalText(1000),
});
export type AwardTerms = z.output<typeof awardTermsSchema>;

/** Karşılaştırmada ve farkta koşulların sırası. */
export const AWARD_TERM_FIELDS = [
  'leadTimeDays',
  'deliveryLocation',
  'deliveryTerms',
  'paymentTerms',
  'warranty',
  'discount',
  'freight',
  'customs',
  'tax',
  'serviceFee',
  'remarks',
] as const satisfies readonly (keyof AwardTerms)[];

function uniqueLines<T extends { rfqLineId: string }>(lines: readonly T[], ctx: z.RefinementCtx) {
  const seen = new Set<string>();
  for (const [index, line] of lines.entries()) {
    if (seen.has(line.rfqLineId)) {
      ctx.addIssue({ code: 'custom', message: 'DUPLICATE_LINE', path: ['lines', index] });
    }
    seen.add(line.rfqLineId);
  }
}

/** Ofisin önerisi: satırlar (en az biri), koşullar ve isteğe bağlı ileti. */
export const awardProposalSchema = z
  .strictObject({
    lines: z
      .array(awardLineSchema, typed('INVALID_LINES'))
      .min(1, 'NO_LINES')
      .max(MAX_AWARD_LINES, 'TOO_MANY_LINES'),
    terms: awardTermsSchema,
    message: optionalText(1000),
  })
  .superRefine((value, ctx) => uniqueLines(value.lines, ctx));
export type AwardProposal = z.output<typeof awardProposalSchema>;

export function parseAwardProposal(input: unknown): ParseResult<AwardProposal> {
  const result = awardProposalSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Karşı teklif satırı: tedarikçi yalnız miktarı, fiyatı ve notu değiştirir. */
export const counterLineSchema = z.strictObject({
  rfqLineId: z.uuid(typed('INVALID_ID')),
  quantity: quantity(),
  unitPrice: money(),
  note: optionalText(500),
});
export type CounterLine = z.output<typeof counterLineSchema>;

/**
 * Tedarikçinin yanıtı: gördüğü turun numarasıyla (bayat tur `STALE_ROUND`). Ret gerekçe ister;
 * karşı teklif satırları ve koşulları taşır.
 */
export const supplierResponseSchema = z.discriminatedUnion(
  'action',
  [
    z.strictObject({
      action: z.literal('accept'),
      roundNo: roundNo(),
      message: optionalText(1000),
    }),
    z.strictObject({
      action: z.literal('reject'),
      roundNo: roundNo(),
      message: requiredText(1000),
    }),
    z
      .strictObject({
        action: z.literal('counter'),
        roundNo: roundNo(),
        lines: z
          .array(counterLineSchema, typed('INVALID_LINES'))
          .min(1, 'NO_LINES')
          .max(MAX_AWARD_LINES, 'TOO_MANY_LINES'),
        terms: awardTermsSchema,
        message: optionalText(1000),
      })
      .superRefine((value, ctx) => uniqueLines(value.lines, ctx)),
  ],
  typed('INVALID_ACTION'),
);
export type SupplierResponse = z.output<typeof supplierResponseSchema>;

export function parseSupplierResponse(input: unknown): ParseResult<SupplierResponse> {
  const result = supplierResponseSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/**
 * Tur durum makinesi. Ofis önerir (ilk tur, karşı teklif ya da retten sonra ve kesinleştirmeden
 * önce kabul edilmiş öneriyi yenilemek için); tedarikçi yalnız ofisin önerisine yanıt verir; ofis
 * yalnız karşı teklifi kabul eder. Kesinleşmiş ve iptal edilmiş ödülde tur yok. Ofisin satır satır
 * seçimi (`select`) yalnız taslaktan kabule gider (ADR 0166 §2).
 */
export function nextAwardStatus(
  current: AwardStatus,
  party: AwardParty,
  action: AwardAction,
): AwardStatus {
  const fail = (): never => {
    throw new DomainError('INVALID_TRANSITION', `${current}:${party}:${action}`);
  };
  if (current === 'finalized' || current === 'cancelled') return fail();
  // Doğrudan ödül: yalnız açılış turunda ve ofis (ADR 0166 §2)
  if (action === 'select') return party === 'office' && current === 'draft' ? 'accepted' : fail();
  if (party === 'office') {
    if (action === 'propose') return 'proposed';
    if (action === 'accept_counter' && current === 'countered') return 'accepted';
    return fail();
  }
  if (current !== 'proposed') return fail();
  if (action === 'accept') return 'accepted';
  if (action === 'counter') return 'countered';
  if (action === 'reject') return 'rejected';
  return fail();
}

/** Yanıt gördüğü tura verilir: arada yeni tur yazıldıysa 409 `STALE_ROUND`. */
export function assertFreshRound(currentRoundNo: number, seenRoundNo: number): void {
  if (currentRoundNo !== seenRoundNo) {
    throw new DomainError('STALE_ROUND', `${seenRoundNo}→${currentRoundNo}`);
  }
}

/** Satır başına tek etkin ödül: başka bir etkin ödüldeki satır `LINE_ALREADY_AWARDED`. */
export function assertLinesAvailable(
  rfqLineIds: readonly string[],
  awardedElsewhere: ReadonlySet<string>,
): void {
  for (const id of rfqLineIds) {
    if (awardedElsewhere.has(id)) throw new DomainError('LINE_ALREADY_AWARDED', id);
  }
}

/**
 * Karşı teklifin satırları önceki turun satırlarına yerleşir: aynı satır kümesi (ekleme ve
 * çıkarma ofisin işidir), ikame seçimi korunur; değişen yalnız miktar, fiyat ve not.
 */
export function mergeCounter(
  previous: readonly AwardLine[],
  counter: readonly CounterLine[],
): AwardLine[] {
  const given = new Map(counter.map((line) => [line.rfqLineId, line]));
  if (given.size !== previous.length || counter.length !== previous.length) {
    throw new DomainError('COUNTER_LINES_MISMATCH');
  }
  return previous.map((line) => {
    const next = given.get(line.rfqLineId);
    if (!next) throw new DomainError('COUNTER_LINES_MISMATCH', line.rfqLineId);
    return { ...line, quantity: next.quantity, unitPrice: next.unitPrice, note: next.note };
  });
}

const sameJson = (a: unknown, b: unknown) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Karşı teklif turu önceki turla aynı satırları ve ikame seçimlerini taşır. */
export function assertCounterShape(
  previous: readonly AwardLine[],
  next: readonly AwardLine[],
): void {
  if (previous.length !== next.length) throw new DomainError('COUNTER_LINES_MISMATCH');
  const byId = new Map(previous.map((line) => [line.rfqLineId, line]));
  for (const line of next) {
    const before = byId.get(line.rfqLineId);
    if (!before || !sameJson(before.substitute, line.substitute)) {
      throw new DomainError('COUNTER_LINES_MISMATCH', line.rfqLineId);
    }
  }
}

/** İki turun satırları ve koşulları aynı mı (kabul ve ret, yanıt verdiği turu birebir taşır). */
export function sameRoundContent(
  a: { lines: readonly AwardLine[]; terms: AwardTerms },
  b: { lines: readonly AwardLine[]; terms: AwardTerms },
): boolean {
  const order = (lines: readonly AwardLine[]) =>
    [...lines].sort((x, y) => x.rfqLineId.localeCompare(y.rfqLineId));
  return sameJson(order(a.lines), order(b.lines)) && sameJson(a.terms, b.terms);
}

/** Kesinleştirme dört gözdür (ADR 0040 #177): ödülün ofis turlarını yazan kişi kesinleştiremez. */
export function assertFinalizer(userId: string, officeRoundAuthors: readonly string[]): void {
  if (officeRoundAuthors.includes(userId)) {
    throw new DomainError('SELF_APPROVAL_FORBIDDEN', userId);
  }
}

const PRECISION = 1e6;
const round = (value: number) => Math.round(value * PRECISION) / PRECISION;

/** Turun toplamı: satırlar (miktar × fiyat), sonra − indirim + navlun + gümrük + vergi + hizmet. */
export function awardTotals(
  lines: readonly Pick<AwardLine, 'quantity' | 'unitPrice'>[],
  terms: Pick<AwardTerms, 'discount' | 'freight' | 'customs' | 'tax' | 'serviceFee'>,
): { itemsTotal: number; total: number } {
  const itemsTotal = round(lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0));
  const total = round(
    itemsTotal - terms.discount + terms.freight + terms.customs + terms.tax + terms.serviceFee,
  );
  return { itemsTotal, total };
}

/** Turun kapalı içeriği (hash'e giren alanlar; mimari belge §7.3). Satırlar RFQ satırına göre. */
export function awardRoundContent(input: {
  awardId: string;
  rfqId: string;
  supplierId: string;
  vesselId: string;
  roundNo: number;
  party: AwardParty;
  action: AwardAction;
  currency: string;
  lines: readonly AwardLine[];
  terms: AwardTerms;
  message: string | null;
}) {
  const lines = [...input.lines]
    .sort((a, b) => a.rfqLineId.localeCompare(b.rfqLineId))
    .map((line) => ({
      rfqLineId: line.rfqLineId,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      substitute: line.substitute
        ? {
            name: line.substitute.name,
            specification: line.substitute.specification,
            unit: line.substitute.unit,
          }
        : null,
      note: line.note,
    }));
  const terms = Object.fromEntries(
    AWARD_TERM_FIELDS.map((field) => [field, input.terms[field] ?? null]),
  ) as AwardTerms;
  return {
    awardId: input.awardId,
    rfqId: input.rfqId,
    supplierId: input.supplierId,
    vesselId: input.vesselId,
    roundNo: input.roundNo,
    party: input.party,
    action: input.action,
    currency: input.currency,
    lines,
    terms,
    message: input.message,
  };
}

export type LineChange = 'added' | 'removed' | 'changed' | 'unchanged';

export interface LineDiff {
  rfqLineId: string;
  change: LineChange;
  quantity: { from: number | null; to: number | null } | null;
  unitPrice: { from: number | null; to: number | null } | null;
  /** İkame seçimi değişti mi (eklendi, kaldırıldı ya da başka ürün). */
  substitute: boolean;
  note: boolean;
  previous: AwardLine | null;
  next: AwardLine | null;
}

export interface TermDiff {
  field: (typeof AWARD_TERM_FIELDS)[number];
  from: unknown;
  to: unknown;
}

export interface RoundDiff {
  lines: LineDiff[];
  terms: TermDiff[];
  changed: boolean;
}

/**
 * İki tur arasındaki fark (sahip: "değişiklikler renkli vurgulansın"): eklenen, çıkarılan, miktarı
 * ya da fiyatı değişen satır ("100 → 150") ve değişen koşullar. Satırlar sonraki turun sırasıyla,
 * çıkarılanlar sonda. İlk turda (`previous` boş) bütün satırlar eklenmiştir.
 */
export function diffRound(
  previous: { lines: readonly AwardLine[]; terms: AwardTerms } | null,
  next: { lines: readonly AwardLine[]; terms: AwardTerms },
): RoundDiff {
  const before = new Map((previous?.lines ?? []).map((line) => [line.rfqLineId, line]));
  const lines: LineDiff[] = [];
  for (const line of next.lines) {
    const old = before.get(line.rfqLineId);
    if (!old) {
      lines.push({
        rfqLineId: line.rfqLineId,
        change: 'added',
        quantity: null,
        unitPrice: null,
        substitute: false,
        note: false,
        previous: null,
        next: line,
      });
      continue;
    }
    const qty = old.quantity !== line.quantity;
    const price = old.unitPrice !== line.unitPrice;
    const substitute = !sameJson(old.substitute, line.substitute);
    const note = (old.note ?? null) !== (line.note ?? null);
    lines.push({
      rfqLineId: line.rfqLineId,
      change: qty || price || substitute || note ? 'changed' : 'unchanged',
      quantity: qty ? { from: old.quantity, to: line.quantity } : null,
      unitPrice: price ? { from: old.unitPrice, to: line.unitPrice } : null,
      substitute,
      note,
      previous: old,
      next: line,
    });
  }
  const kept = new Set(next.lines.map((line) => line.rfqLineId));
  for (const line of previous?.lines ?? []) {
    if (kept.has(line.rfqLineId)) continue;
    lines.push({
      rfqLineId: line.rfqLineId,
      change: 'removed',
      quantity: null,
      unitPrice: null,
      substitute: false,
      note: false,
      previous: line,
      next: null,
    });
  }
  const terms: TermDiff[] = previous
    ? AWARD_TERM_FIELDS.filter((field) => !sameJson(previous.terms[field], next.terms[field])).map(
        (field) => ({ field, from: previous.terms[field] ?? null, to: next.terms[field] ?? null }),
      )
    : [];
  return {
    lines,
    terms,
    changed: terms.length > 0 || lines.some((line) => line.change !== 'unchanged'),
  };
}
